/*
Copyright 2023, 2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import {
  AudioPresets,
  BackupCodecPolicy,
  DefaultReconnectPolicy,
  type RoomOptions,
  type TrackPublishDefaults,
  type VideoPreset,
  VideoPresets,
} from "livekit-client";

const defaultLiveKitPublishOptions: TrackPublishDefaults = {
  audioPreset: AudioPresets.music,
  dtx: true,
  // disable red because the livekit server strips out red packets for clients
  // that don't support it (firefox) but of course that doesn't work with e2ee.
  red: false,
  forceStereo: false,
  simulcast: true,

  // Primary codec: AV1 with SVC for best quality at given bitrate
  videoCodec: "av1",
  scalabilityMode: "L3T3_KEY",

  // Higher bitrate for better video quality (4 Mbps, up from ~1.5 Mbps)
  videoEncoding: {
    maxBitrate: 4_000_000,
    maxFramerate: 30,
  },

  // Simulcast layers for adaptive streaming
  videoSimulcastLayers: [VideoPresets.h180, VideoPresets.h360] as VideoPreset[],

  // Fallback for clients without AV1 support (e.g., older Firefox)
  backupCodec: {
    codec: "vp8",
    encoding: {
      maxBitrate: 2_000_000,
      maxFramerate: 30,
    },
  },
  backupCodecPolicy: BackupCodecPolicy.PREFER_PRIMARY,

  // Screenshare: higher quality for code/text readability
  screenShareEncoding: {
    maxBitrate: 6_000_000,
    maxFramerate: 60,
  },

  stopMicTrackOnMute: false,
} as const;

export const defaultLiveKitOptions: RoomOptions = {
  // automatically manage subscribed video quality
  adaptiveStream: true,

  // optimize publishing bandwidth and CPU for published tracks
  dynacast: true,

  // capture settings - 1080p for higher base quality
  videoCaptureDefaults: {
    resolution: {
      width: 1920,
      height: 1080,
      frameRate: 30,
    },
  },

  // publish settings
  publishDefaults: defaultLiveKitPublishOptions,

  // default LiveKit options that seem to be sane
  stopLocalTrackOnUnpublish: true,
  reconnectPolicy: new DefaultReconnectPolicy(),
  disconnectOnPageLeave: true,
  webAudioMix: false,
};
